import { CHARACTER_ACTIONS, type CharacterActionKey } from "@/character/actions";
import type { SheetLayout } from "@/character/grid";
import { startRowLoop } from "@/character/sheet-preview-canvas";
import { getGameSettings } from "@/game/model/game-settings";
import { SHADOW_DISPLAY_ALPHA, SHADOW_SCALE_MULTIPLIER } from "@/shadow/constants";
import type { ShadowDefinition } from "@/shadow/types";

const DEFAULT_PREVIEW_ACTION: CharacterActionKey = "idle";

function actionRow(key: CharacterActionKey): number {
  return CHARACTER_ACTIONS.find((a) => a.key === key)?.row ?? 0;
}

function previewFps(key: CharacterActionKey): number {
  if (key === "run") return 12;
  if (key === "attack") return getGameSettings().playerAttackAnimFps;
  return 8;
}

function shadowSheetLayout(shadow: ShadowDefinition): SheetLayout {
  const grid = shadow.grid;
  const sheetWidth = grid.regionX + grid.columns * grid.frameWidth;
  const sheetHeight = grid.regionY + grid.rows * grid.frameHeight;
  return { grid, sheetWidth, sheetHeight };
}

export type ShadowIdlePreviewOptions = {
  displaySizePx?: number;
  /** Match in-game equipped shadow (2× scale, 60% opacity). */
  equippedStyle?: boolean;
  /** IDLE / RUN / SIT / ATTACK buttons under the canvas. */
  showActionButtons?: boolean;
};

export function mountShadowSpritePreview(
  host: HTMLElement,
  shadow: ShadowDefinition,
  options?: ShadowIdlePreviewOptions,
): () => void {
  const basePx = options?.displaySizePx ?? 72;
  const displaySize = options?.equippedStyle
    ? Math.round(basePx * SHADOW_SCALE_MULTIPLIER)
    : basePx;
  const showActionButtons = options?.showActionButtons ?? false;

  const actionButtons = showActionButtons
    ? `<div class="shadow-preview-actions" role="group" aria-label="ลองแอนิเมชัน">
        ${CHARACTER_ACTIONS
          .map(
            (action) =>
              `<button type="button" class="btn btn-ghost btn-xs shadow-preview-action${action.key === DEFAULT_PREVIEW_ACTION ? " shadow-preview-action--active" : ""}" data-shadow-preview-action="${action.key}">${action.templateLabel}</button>`,
          )
          .join("")}
      </div>`
    : "";

  host.innerHTML = `
    <div class="shadow-preview-root">
      <div class="shadow-preview-stage${options?.equippedStyle ? " shadow-preview-stage--equipped" : ""}">
        <canvas class="shadow-preview-canvas" role="img" aria-label="${shadow.name} ${DEFAULT_PREVIEW_ACTION}"></canvas>
      </div>
      ${actionButtons}
    </div>
  `;

  const stage = host.querySelector<HTMLElement>(".shadow-preview-stage");
  const canvas = host.querySelector<HTMLCanvasElement>(".shadow-preview-canvas");
  if (options?.equippedStyle && stage) {
    stage.style.opacity = String(SHADOW_DISPLAY_ALPHA);
  }

  let stopLoop: (() => void) | undefined;
  let activeAction: CharacterActionKey = DEFAULT_PREVIEW_ACTION;
  let layout: SheetLayout | null = null;
  let img: HTMLImageElement | null = null;

  const setActiveButton = (key: CharacterActionKey) => {
    host.querySelectorAll<HTMLElement>("[data-shadow-preview-action]").forEach((btn) => {
      btn.classList.toggle(
        "shadow-preview-action--active",
        btn.dataset.shadowPreviewAction === key,
      );
    });
  };

  const playAction = (key: CharacterActionKey) => {
    if (!canvas || !img || !layout) return;
    stopLoop?.();
    activeAction = key;
    setActiveButton(key);
    canvas.setAttribute("aria-label", `${shadow.name} ${key}`);
    const row = actionRow(key);
    const mode = key === "attack" ? "once" : "loop";
    const afterOnce =
      key === "attack"
        ? () => {
            playAction("idle");
          }
        : undefined;
    stopLoop = startRowLoop(canvas, img, layout, row, previewFps(key), mode, afterOnce);
  };

  host.querySelectorAll<HTMLButtonElement>("[data-shadow-preview-action]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const key = btn.dataset.shadowPreviewAction as CharacterActionKey | undefined;
      if (!key) return;
      playAction(key);
    });
  });

  const probe = new Image();
  probe.decoding = "async";
  probe.onload = () => {
    if (!canvas) return;
    img = probe;
    layout = shadowSheetLayout(shadow);
    canvas.style.width = `${displaySize}px`;
    canvas.style.height = `${displaySize}px`;
    playAction(activeAction);
  };
  probe.src = shadow.spritesheetDataUrl;

  return () => {
    stopLoop?.();
    host.replaceChildren();
  };
}

/** Card thumbnail — idle loop only. */
export function mountShadowIdlePreview(
  host: HTMLElement,
  shadow: ShadowDefinition,
  options?: ShadowIdlePreviewOptions,
): () => void {
  return mountShadowSpritePreview(host, shadow, {
    ...options,
    showActionButtons: false,
  });
}
